import * as signalR from '@microsoft/signalr';
import { ONCALL_OVERRIDE_CHANGED_EVENT } from '../shared/onCallOverrideEvents';
import { ONCALL_PRESENCE_CHANGED_EVENT } from '../shared/onCallPresenceEvents';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const SIGNALR_NEGOTIATE_URL = import.meta.env.VITE_SIGNALR_HUB_URL || `${API_BASE_URL}/api/signalr/negotiate`;

type AlertCallback = (alert: any) => void;
type AlertIdCallback = (alertId: string) => void;
type NotificationInboxChangedCallback = (payload: any) => void;
type DebugPayload = {
  kind: string;
  message: string;
  tenantId?: string | null;
  userId?: string | null;
  state?: string;
  alertId?: string;
  eventName?: string;
  timestamp: string;
};

const pick = (obj: any, ...keys: string[]) => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return undefined;
};

const normalizeGuid = (value: any): string =>
  String(value || '')
    .trim()
    .replace(/[{}]/g, '')
    .toLowerCase();

const normalizeAlertPayload = (payload: any): any => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const timelineRaw = pick(payload, 'timeline', 'Timeline') || [];
  const timeline = Array.isArray(timelineRaw)
    ? timelineRaw.map((event: any) => ({
        ...event,
        type: pick(event, 'type', 'Type') || 'Update',
        timestamp: pick(event, 'timestamp', 'Timestamp') || new Date().toISOString(),
        description: pick(event, 'description', 'Description') || '',
        createdBy: pick(event, 'createdBy', 'CreatedBy'),
      }))
    : [];

  return {
    ...payload,
    alertId: pick(payload, 'alertId', 'AlertId'),
    status: pick(payload, 'status', 'Status'),
    severity: pick(payload, 'severity', 'Severity'),
    priority: pick(payload, 'priority', 'Priority'),
    service: pick(payload, 'service', 'Service'),
    source: pick(payload, 'source', 'Source'),
    title: pick(payload, 'title', 'Title'),
    description: pick(payload, 'description', 'Description'),
    createdAt: pick(payload, 'createdAt', 'CreatedAt'),
    updatedAt: pick(payload, 'updatedAt', 'UpdatedAt'),
    ackAt: pick(payload, 'ackAt', 'AckAt'),
    ackBy: pick(payload, 'ackBy', 'AckBy'),
    closedAt: pick(payload, 'closedAt', 'ClosedAt'),
    closedBy: pick(payload, 'closedBy', 'ClosedBy'),
    timeline,
    escalationInfo: pick(payload, 'escalationInfo', 'EscalationInfo'),
  };
};

export { ONCALL_OVERRIDE_CHANGED_EVENT };

export class SignalRService {
  private static instance: SignalRService;
  private connection: signalR.HubConnection | null = null;
  private connectingPromise: Promise<void> | null = null;
  private connectedTenantId: string | null = null;
  private connectedUserId: string | null = null;
  private alertCreatedCallbacks: AlertCallback[] = [];
  private alertUpdatedCallbacks: AlertCallback[] = [];
  private alertDeletedCallbacks: AlertIdCallback[] = [];
  private notificationInboxChangedCallbacks: NotificationInboxChangedCallback[] = [];
    private inboundNotificationChangedCallbacks: NotificationInboxChangedCallback[] = [];
  private workflowProgressCallbacks: NotificationInboxChangedCallback[] = [];
  private deliveryIncidentsChangedCallbacks: NotificationInboxChangedCallback[] = [];
  private lastInboxEventKey: string | null = null;
  private lastInboxEventAtMs = 0;

  private emitDebug(payload: Omit<DebugPayload, 'timestamp'>): void {
    const entry: DebugPayload = {
      ...payload,
      timestamp: new Date().toISOString(),
    };

    try {
      (window as any).__nogSignalR = {
        ...(window as any).__nogSignalR,
        last: entry,
      };
      window.dispatchEvent(new CustomEvent('nog:signalr:debug', { detail: entry }));
      console.info(`[SignalR:${entry.kind}] ${entry.message}`, entry);
    } catch {
      // no-op
    }
  }

  private safeDispatchAlert(callbacks: AlertCallback[], alert: any): void {
    callbacks.forEach((cb) => {
      try {
        cb(alert);
      } catch (err) {
        this.emitDebug({
          kind: 'callback-error',
          message: 'Alert callback threw error',
          alertId: normalizeGuid(alert?.alertId),
          state: this.connection?.state,
        });
        console.error('SignalR alert callback error:', err);
      }
    });
  }

  private safeDispatchAlertId(callbacks: AlertIdCallback[], alertId: string): void {
    callbacks.forEach((cb) => {
      try {
        cb(alertId);
      } catch (err) {
        this.emitDebug({
          kind: 'callback-error',
          message: 'AlertId callback threw error',
          alertId,
          state: this.connection?.state,
        });
        console.error('SignalR alertId callback error:', err);
      }
    });
  }

  private safeDispatchNotificationInboxChanged(callbacks: NotificationInboxChangedCallback[], payload: any): void {
    callbacks.forEach((cb) => {
      try {
        cb(payload);
      } catch (err) {
        this.emitDebug({
          kind: 'callback-error',
          message: 'Notification inbox callback threw error',
          state: this.connection?.state,
        });
        console.error('SignalR notification callback error:', err);
      }
    });
  }

  private shouldDispatchInboxChanged(payload: any): boolean {
    const actorUserId = normalizeGuid(pick(payload, 'actorUserId', 'ActorUserId'));
    const notificationId = normalizeGuid(pick(payload, 'notificationId', 'NotificationId'));
    const action = String(pick(payload, 'action', 'Action') || '').trim().toLowerCase();
    const scope = String(pick(payload, 'scope', 'Scope') || '').trim().toLowerCase();
    const key = `${actorUserId}|${notificationId}|${action}|${scope}`;
    const now = Date.now();

    // Suppress immediate duplicates caused by fanout retries / duplicate handlers.
    if (this.lastInboxEventKey === key && now - this.lastInboxEventAtMs < 2000) {
      return false;
    }

    this.lastInboxEventKey = key;
    this.lastInboxEventAtMs = now;
    return true;
  }

  private constructor() {}

  static getInstance(): SignalRService {
    if (!SignalRService.instance) {
      SignalRService.instance = new SignalRService();
    }
    return SignalRService.instance;
  }

  async connect(tenantId: string, userId: string): Promise<void> {
    if (
      this.connection?.state === signalR.HubConnectionState.Connected &&
      this.connectedTenantId === tenantId &&
      this.connectedUserId === userId
    ) {
      return;
    }

    if (this.connectingPromise) {
      return this.connectingPromise;
    }

    this.connectingPromise = this.startConnection(tenantId, userId);
    try {
      await this.connectingPromise;
    } finally {
      this.connectingPromise = null;
    }
  }

  async ensureConnected(tenantId: string, userId: string): Promise<void> {
    const state = this.connection?.state ?? signalR.HubConnectionState.Disconnected;
    if (
      state === signalR.HubConnectionState.Connected &&
      this.connectedTenantId === tenantId &&
      this.connectedUserId === userId
    ) {
      return;
    }
    if (state === signalR.HubConnectionState.Connecting || state === signalR.HubConnectionState.Reconnecting) {
      return;
    }
    await this.connect(tenantId, userId);
  }

  private async startConnection(tenantId: string, userId: string): Promise<void> {
    if (
      this.connection &&
      this.connection.state !== signalR.HubConnectionState.Disconnected
    ) {
      try {
        await this.connection.stop();
      } catch {
        // Ignore stop errors before reconnect.
      }
    }

    const negotiate = await this.negotiate();

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(negotiate.url, {
        accessTokenFactory: async () => (await this.negotiate()).accessToken,
      })
      .withAutomaticReconnect([0, 1000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onclose(() => {
      this.connectedTenantId = null;
      this.connectedUserId = null;
      this.emitDebug({
        kind: 'closed',
        message: 'Connection closed',
        state: this.connection?.state,
      });
    });

    this.connection.onreconnecting(() => {
      this.emitDebug({
        kind: 'reconnecting',
        message: 'Connection reconnecting',
        state: this.connection?.state,
        tenantId: this.connectedTenantId,
        userId: this.connectedUserId,
      });
    });

    this.connection.onreconnected(() => {
      this.emitDebug({
        kind: 'reconnected',
        message: 'Connection reconnected',
        state: this.connection?.state,
        tenantId: this.connectedTenantId,
        userId: this.connectedUserId,
      });
    });

    this.connection.on('AlertCreated', (alert: any) => {
      const normalized = normalizeAlertPayload(alert);
      this.emitDebug({
        kind: 'event',
        message: 'AlertCreated received',
        eventName: 'AlertCreated',
        alertId: normalizeGuid(normalized?.alertId),
        state: this.connection?.state,
      });
      this.safeDispatchAlert(this.alertCreatedCallbacks, normalized);
    });

    this.connection.on('AlertUpdated', (alert: any) => {
      const normalized = normalizeAlertPayload(alert);
      this.emitDebug({
        kind: 'event',
        message: 'AlertUpdated received',
        eventName: 'AlertUpdated',
        alertId: normalizeGuid(normalized?.alertId),
        state: this.connection?.state,
      });
      this.safeDispatchAlert(this.alertUpdatedCallbacks, normalized);
    });

    this.connection.on('AlertDeleted', (alertId: string) => {
      const normalized = normalizeGuid(alertId);
      this.emitDebug({
        kind: 'event',
        message: 'AlertDeleted received',
        eventName: 'AlertDeleted',
        alertId: normalized,
        state: this.connection?.state,
      });
      this.safeDispatchAlertId(this.alertDeletedCallbacks, normalized);
    });

    this.connection.on('NotificationInboxChanged', (payload: any) => {
      if (!this.shouldDispatchInboxChanged(payload)) {
        this.emitDebug({
          kind: 'event-skipped',
          message: 'NotificationInboxChanged duplicate skipped',
          eventName: 'NotificationInboxChanged',
          state: this.connection?.state,
        });
        return;
      }

      this.emitDebug({
        kind: 'event',
        message: 'NotificationInboxChanged received',
        eventName: 'NotificationInboxChanged',
        state: this.connection?.state,
      });
      this.safeDispatchNotificationInboxChanged(this.notificationInboxChangedCallbacks, payload);
    });
    this.connection.on('InboundNotificationChanged', (payload: any) => {
      this.emitDebug({ kind: 'event', message: 'InboundNotificationChanged received',
        eventName: 'InboundNotificationChanged', state: this.connection?.state });
      this.safeDispatchNotificationInboxChanged(this.inboundNotificationChangedCallbacks, payload);
    });
    this.connection.on('WorkflowProgress', (payload: any) => {
      this.emitDebug({ kind: 'event', message: 'WorkflowProgress received', eventName: 'WorkflowProgress',
        alertId: normalizeGuid(payload?.alertId), state: this.connection?.state });
      this.safeDispatchNotificationInboxChanged(this.workflowProgressCallbacks, payload);
    });
    this.connection.on('NotificationDeliveryIncidentsChanged', (payload: any) => {
      this.emitDebug({ kind: 'event', message: 'NotificationDeliveryIncidentsChanged received',
        eventName: 'NotificationDeliveryIncidentsChanged', state: this.connection?.state });
      this.safeDispatchNotificationInboxChanged(this.deliveryIncidentsChangedCallbacks, payload);
    });
    this.connection.on('SchedulesChanged', () => {
      this.emitDebug({ kind: 'event', message: 'SchedulesChanged received', eventName: 'SchedulesChanged',
        state: this.connection?.state });
      try {
        globalThis.window?.dispatchEvent(new CustomEvent('nog:schedules:changed', { detail: { source: 'signalr' } }));
      } catch (error) {
        console.error('Failed to dispatch schedules changed event', error);
      }
    });
    this.connection.on('oncallOverrideChanged', (payload: any) => {
      this.emitDebug({ kind: 'event', message: 'oncallOverrideChanged received', eventName: 'oncallOverrideChanged',
        state: this.connection?.state });
      try {
        globalThis.window?.dispatchEvent(new CustomEvent(ONCALL_OVERRIDE_CHANGED_EVENT, { detail: payload ?? {} }));
      } catch (error) {
        console.error('Failed to dispatch on-call override changed event', error);
      }
    });

    this.connection.on('oncallPresenceChanged', (payload: any) => {
      this.emitDebug({ kind: 'event', message: 'oncallPresenceChanged received', eventName: 'oncallPresenceChanged',
        state: this.connection?.state });
      try {
        globalThis.window?.dispatchEvent(new CustomEvent(ONCALL_PRESENCE_CHANGED_EVENT, { detail: payload ?? {} }));
      } catch (error) {
        console.error('Failed to dispatch on-call presence changed event', error);
      }
    });

    try {
      await this.connection.start();
      this.connectedTenantId = tenantId;
      this.connectedUserId = userId;
      this.emitDebug({
        kind: 'connected',
        message: 'Connection started',
        tenantId,
        userId,
        state: this.connection?.state,
      });
      console.log(`SignalR connected for tenant ${tenantId} user ${userId}`);
    } catch (err) {
      this.connectedTenantId = null;
      this.connectedUserId = null;
      console.error('SignalR connection failed:', err);
      this.emitDebug({ kind: 'connection-failed', message: 'Connection start failed', state: this.connection?.state });
      throw err;
    }
  }

  private async negotiate(): Promise<{ url: string; accessToken: string }> {
    const authToken = this.getStoredAccessToken();
    if (!authToken) {
      this.emitDebug({
        kind: 'auth-missing',
        message: 'No auth token available for SignalR negotiate',
        state: this.connection?.state,
      });
      throw new Error('No auth token available for SignalR negotiate');
    }

    const response = await fetch(SIGNALR_NEGOTIATE_URL, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      this.emitDebug({
        kind: 'negotiate-failed',
        message: `SignalR negotiate failed (${response.status})`,
        state: this.connection?.state,
      });
      throw new Error(`SignalR negotiate failed (${response.status})`);
    }

    const payload = await response.json() as any;
    const body = payload?.value || payload?.Value || payload;
    const url = body?.url || body?.Url;
    const accessToken = body?.accessToken || body?.AccessToken;

    if (!url || !accessToken) {
      this.emitDebug({
        kind: 'negotiate-invalid',
        message: 'SignalR negotiate response missing url/accessToken',
        state: this.connection?.state,
      });
      throw new Error('SignalR negotiate response missing url/accessToken');
    }

    return { url, accessToken };
  }

  private getStoredAccessToken(): string | null {
    try {
      const authStateRaw = sessionStorage.getItem('auth_state');
      if (authStateRaw) {
        const parsed = JSON.parse(authStateRaw) as { token?: string };
        if (parsed?.token) return parsed.token;
      }

      const authToken = localStorage.getItem('authToken');
      if (authToken) return authToken;
    } catch {
      // ignore and return null below
    }

    return null;
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
    }
  }

  onAlertCreated(callback: AlertCallback): () => void {
    this.alertCreatedCallbacks.push(callback);
    return () => this.offAlertCreated(callback);
  }

  offAlertCreated(callback: AlertCallback): void {
    this.alertCreatedCallbacks = this.alertCreatedCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  onAlertUpdated(callback: AlertCallback): () => void {
    this.alertUpdatedCallbacks.push(callback);
    return () => this.offAlertUpdated(callback);
  }

  offAlertUpdated(callback: AlertCallback): void {
    this.alertUpdatedCallbacks = this.alertUpdatedCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  onAlertDeleted(callback: AlertIdCallback): () => void {
    this.alertDeletedCallbacks.push(callback);
    return () => this.offAlertDeleted(callback);
  }

  offAlertDeleted(callback: AlertIdCallback): void {
    this.alertDeletedCallbacks = this.alertDeletedCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  onNotificationInboxChanged(callback: NotificationInboxChangedCallback): () => void {
    this.notificationInboxChangedCallbacks.push(callback);
    return () => this.offNotificationInboxChanged(callback);
  }

  offNotificationInboxChanged(callback: NotificationInboxChangedCallback): void {
    this.notificationInboxChangedCallbacks = this.notificationInboxChangedCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  getConnectionState(): signalR.HubConnectionState | 'disconnected' {
    return this.connection?.state ?? 'disconnected';
  }

  onInboundNotificationChanged(callback: NotificationInboxChangedCallback): () => void {
    this.inboundNotificationChangedCallbacks.push(callback);
    return () => {
      this.inboundNotificationChangedCallbacks = this.inboundNotificationChangedCallbacks.filter(current => current !== callback);
    };
  }

  onWorkflowProgress(callback: (payload: { alertId?: string; instanceId?: string; eventType?: string }) => void): () => void {
    this.workflowProgressCallbacks.push(callback);
    return () => {
      this.workflowProgressCallbacks = this.workflowProgressCallbacks.filter(current => current !== callback);
    };
  }

  onNotificationDeliveryIncidentsChanged(callback: NotificationInboxChangedCallback): () => void {
    this.deliveryIncidentsChangedCallbacks.push(callback);
    return () => {
      this.deliveryIncidentsChangedCallbacks = this.deliveryIncidentsChangedCallbacks.filter(current => current !== callback);
    };
  }
}
